import { Button, HFlow, Icon, Modal, ModalBody, ModalFooter, ModalHeader, Text, Tooltip, VFlow } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import clearTypename from 'graphql/clearTypename'
import {
  useForcaAtualizacaoCidadaoCadsusLazyQuery,
  useSincronizaCidadaoCadsusPollingQuery,
} from 'graphql/hooks.generated'
import { SincronizacaoCadsusResultEnum } from 'graphql/types.generated'
import useAtmosphere from 'hooks/useAtmosphere'
import React, { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  ModalBuscaInformacoesCadsus,
  ModalBuscaInformacoesContentState,
} from 'view/cidadao/create/cadsus/components/ModalBuscaInformacoesCadsus'
import { CadsusAtmosphereResponse, ResultSyncCadsusType } from 'view/cidadao/create/cadsus/types/model-cadsus'

import { convertCadsusCidadaoLocalTypeToFormModel } from '../../converter-cidadao'
import { CidadaoFormModel } from '../../types/model-cidadao'

interface CidadaoFciSincronizarCadsusButtonProps {
  cns: string
  cidadaoId: string
  setCidadaoCadsus(value: React.SetStateAction<CidadaoFormModel>): void
  needLoginGovbr: boolean
  isCadsusHabilitado: boolean
}

interface ModalConfirmarBuscaDadosProps {
  cns: string
}

let buscaCidadaoAtualizadoPolling = false

const POLLING_INTERVAL_TIME_1S = 1 * 1000 // 1 segundo

export function CidadaoFciSincronizarCadsusButton(props: Readonly<CidadaoFciSincronizarCadsusButtonProps>) {
  const { cns, cidadaoId, setCidadaoCadsus, needLoginGovbr, isCadsusHabilitado } = props
  const [modalConfirmaBuscaDadosState, setModalConfirmaBuscaDadosState] = useState(false)

  const [isModalBuscaCidadaoOpenState, setIsModalBuscaCidadaoOpenState] = useState<boolean>(false)
  const [resultBuscaInfoCidadaoState, setResultBuscaInfoCidadaoState] = useState<ModalBuscaInformacoesContentState>({
    isLoading: false,
  })
  const [uuidBuscaCidadaoAtualizado, setUuidBuscaCidadaoAtualizado] = useState<string>()

  const [queryForcaAtualizacaoCidadaoCadsus] = useForcaAtualizacaoCidadaoCadsusLazyQuery()
  const { data: sessao } = useSession()
  const handleRejection = useErrorHandler()

  const atualizaResultadoBuscaCidadao = useCallback(
    (data: ResultSyncCadsusType) => {
      buscaCidadaoAtualizadoPolling = false
      if (
        data.result === SincronizacaoCadsusResultEnum.SUCESSO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_CADSUS_DESABILITADO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_SEM_CONEXAO ||
        data.result === SincronizacaoCadsusResultEnum.ERRO_CERTIFICADO_DESABILITADO
      ) {
        setIsModalBuscaCidadaoOpenState(false)
      } else {
        setResultBuscaInfoCidadaoState({
          cns: data.cidadaoCadsus?.cns,
          cpf: data.cidadaoCadsus?.cpf,
          errors: data.errors,
          statusSincronizacao: data.result,
          isLoading: false,
        })
      }
      setCidadaoCadsus(convertCadsusCidadaoLocalTypeToFormModel(clearTypename(data.cidadaoLocal)))
    },
    [setCidadaoCadsus]
  )

  const { refetch: buscaCadsusPollingRefetch } = useSincronizaCidadaoCadsusPollingQuery({ skip: true })

  useEffect(() => {
    let shouldUpdateBuscaCidadaoAtualizado = true

    const scheduleCadsusPolling = setInterval(() => {
      if (!!uuidBuscaCidadaoAtualizado && buscaCidadaoAtualizadoPolling && shouldUpdateBuscaCidadaoAtualizado) {
        shouldUpdateBuscaCidadaoAtualizado = false
        buscaCadsusPollingRefetch({ uuid: uuidBuscaCidadaoAtualizado })
          .then(
            ({ data }) =>
              !!data.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidBuscaCidadaoAtualizado &&
              buscaCidadaoAtualizadoPolling &&
              atualizaResultadoBuscaCidadao(data.sincronizacaoCadsusPolling)
          )
          .finally(() => (shouldUpdateBuscaCidadaoAtualizado = true))
          .catch(handleRejection)
      }
    }, POLLING_INTERVAL_TIME_1S)

    return () => clearInterval(scheduleCadsusPolling)
  }, [atualizaResultadoBuscaCidadao, buscaCadsusPollingRefetch, handleRejection, uuidBuscaCidadaoAtualizado])

  const fetchCadsusResult = useCallback(
    (body: CadsusAtmosphereResponse) => {
      if (body.uuid === uuidBuscaCidadaoAtualizado && buscaCidadaoAtualizadoPolling) {
        buscaCadsusPollingRefetch({ uuid: uuidBuscaCidadaoAtualizado })
          .then(
            ({ data }) =>
              !!data.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidBuscaCidadaoAtualizado &&
              buscaCidadaoAtualizadoPolling &&
              atualizaResultadoBuscaCidadao(data.sincronizacaoCadsusPolling)
          )
          .catch(handleRejection)
      }
    },
    [atualizaResultadoBuscaCidadao, buscaCadsusPollingRefetch, handleRejection, uuidBuscaCidadaoAtualizado]
  )

  useAtmosphere<CadsusAtmosphereResponse>({
    topic: `cadsus-atualizacao-forcada/${sessao.profissional.usuario.id}`,
    onMessage: fetchCadsusResult,
  })

  const handleClickSincronizar = () => {
    setModalConfirmaBuscaDadosState(true)
  }

  const handleCloseModal = () => {
    setModalConfirmaBuscaDadosState(false)
  }

  const handleClickContiuar = () => {
    const hash = uuidv4()
    buscaCidadaoAtualizadoPolling = true
    queryForcaAtualizacaoCidadaoCadsus({
      variables: { input: { cidadao: cidadaoId, uuid: hash } },
    })
    setUuidBuscaCidadaoAtualizado(hash)
    setResultBuscaInfoCidadaoState({ ...resultBuscaInfoCidadaoState, isLoading: true })
    setModalConfirmaBuscaDadosState(false)
    setIsModalBuscaCidadaoOpenState(true)
  }

  const botaoSincronizarDisabled = !isCadsusHabilitado || needLoginGovbr

  return (
    <>
      <Tooltip
        text={
          !botaoSincronizarDisabled
            ? 'Para sincronizar com o CADSUS, é necessário configurar o certificado e estar logado através do gov.br.'
            : 'Para sincronizar com o CADSUS, é necessário configurar o certificado e estar logado através do gov.br. Entre em contato com o administrador municipal.'
        }
      >
        <Button onClick={handleClickSincronizar} kind='primary' disabled={botaoSincronizarDisabled}>
          <Icon icon='sync' />
          Sincronizar com Cadsus
        </Button>
      </Tooltip>

      <Modal open={modalConfirmaBuscaDadosState} onClose={handleCloseModal} size='small'>
        <ModalHeader title='Deseja sincronizar com o CADSUS?' icon={{ name: 'questionMarkFilled', fill: 'info' }} />
        <ConfirmarBuscaDadosModalBody cns={cns} />
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button onClick={handleCloseModal}>Cancelar</Button>
            <Button onClick={handleClickContiuar} kind='primary'>
              Continuar
            </Button>
          </HFlow>
        </ModalFooter>
      </Modal>

      <ModalBuscaInformacoesCadsus
        result={resultBuscaInfoCidadaoState}
        isModalOpen={isModalBuscaCidadaoOpenState}
        setModalState={setIsModalBuscaCidadaoOpenState}
        isLoading={resultBuscaInfoCidadaoState.isLoading}
      />
    </>
  )
}

function ConfirmarBuscaDadosModalBody(props: Readonly<ModalConfirmarBuscaDadosProps>) {
  return (
    <ModalBody>
      <VFlow>
        <Text>
          Os dados do cadastro de CNS {props.cns} no CADSUS serão carregados para esta tela. As alterações só serão
          efetivadas após salvar o cadastro.
        </Text>
      </VFlow>
    </ModalBody>
  )
}
